// Angular imports
import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';

// Rxjs imports
import { Subscription } from 'rxjs';

// 3rd party imports
import { DxFormComponent } from 'devextreme-angular';
import * as moment from 'moment-timezone';
import notify from 'devextreme/ui/notify';
import { plainToClass } from 'class-transformer';

// CC-Common imports
import { IconConstants } from 'cc-common';

// Service imports
import { ServerErrorService } from '../../../_services/shared-services/server-error.service';
import { ContextManagerService } from '../../../_services/data-services/context-manager.service';
import { UserService } from '../../../_services/data-services/user.service';
import { AlertsService } from '../../_services/alerts.service';
import { ScreenService } from '../../../_services/shared-services/screen.service';

// Model imports
import { PropertyCommunityInfoResultDto } from '../../../_models/property-community-info-result-dto';
import { UserInfoResultDto } from '../../../_models/user-info-result-dto';
import { AddEditOwnerAlertParameterDto } from '../../_models/add-edit-owner-alert-parameter-dto';
import { AddEditCommunityAlertParameterDto } from '../../_models/add-edit-community-alert-parameter-dto';
import { AlertResultDto } from '../../_models/alert-result-dto';
import { StatusMessageResultDto } from '../../../_models/status-message-result-dto';

// Constant imports
import { MessageInterval } from '../../../_constants/messages-intervals';

@Component({
    selector: 'app-community-owner-alerts-editor',
    templateUrl: './community-owner-alerts-editor.component.html',
    styleUrls: ['./community-owner-alerts-editor.component.scss']
})
export class CommunityOwnerAlertsEditorComponent implements OnInit {
    @ViewChild('alertForm', { static: false }) alertForm: DxFormComponent;
    @Output() onSuccess: EventEmitter<boolean> = new EventEmitter();

    visible = false;
    loadingVisible = false;
    isOwnerContext = false;
    isSmallScreen = false;
    popupTitle: string;
    popupWidth = '35%';
    popupHeight = '55%';

    formParam: AddEditOwnerAlertParameterDto | AddEditCommunityAlertParameterDto;
    priorities: { id: number; text: string }[];

    iconOk = IconConstants.CONFIRM_YES;
    iconCancel = IconConstants.CONFIRM_CANCEL;

    loggedInUser: UserInfoResultDto;
    selectedContext: PropertyCommunityInfoResultDto;
    contextChangeSubscription: Subscription;

    constructor(
        private readonly screenService: ScreenService,
        private readonly errorService: ServerErrorService,
        private readonly contextService: ContextManagerService,
        private readonly userService: UserService,
        private readonly alertsService: AlertsService
    ) {
        this.onEndDateFlagChange = this.onEndDateFlagChange.bind(this);
    }

    ngOnInit(): void {
        this.loggedInUser = this.userService.getCurrentUserInfo();
        this.selectedContext = this.contextService.getSelectedContext();
        this.contextChangeSubscription = this.contextService.getContext().subscribe(() => {
            this.selectedContext = this.contextService.getSelectedContext();
        });
        this.priorities = [
            { id: 1, text: 'Normal' },
            { id: 5, text: 'High' }
        ];

        this.updateScreenSize();
        this.screenService.changed.subscribe(this.updateScreenSize.bind(this));
    }

    show(isOwnerContext = false, result?: AlertResultDto): void {
        this.isOwnerContext = isOwnerContext;
        if (isOwnerContext) {
            this.popupTitle = `Add Owner Alert - ${this.selectedContext.customerId}`;
        } else {
            this.popupTitle = `Add Community Alert`;
        }

        let formParam: any = new AddEditCommunityAlertParameterDto();
        if (isOwnerContext) {
            formParam = new AddEditOwnerAlertParameterDto();
            formParam.customerID = this.selectedContext.customerId;
        }
        formParam.communityID = this.selectedContext.communityId;
        formParam.community = this.selectedContext.communityName;
        formParam.createdBy = this.loggedInUser.displayName;
        formParam.noEndDate = false;

        if (!!result) {
            formParam.alertID = result.alertID;
            formParam.priority = result.priority.toLowerCase() === 'high' ? 5 : 1;
            formParam.effectiveDate = result.effectiveDate;
            formParam.endDate =
                result.expireAfterDays !== 99999
                    ? moment(result.effectiveDate).add(result.expireAfterDays, 'days').toDate()
                    : null;
            formParam.noEndDate = result.expireAfterDays === 99999;
            formParam.subject = result.subject;
            formParam.text = result.text;
        }

        this.formParam = formParam;
        this.visible = true;
    }

    onShown(e): void {
        e.component.repaint();
    }

    onHidden(): void {
        this.visible = false;
    }

    onOkClick(): void {
        const validator = this.alertForm.instance.validate();
        if (validator.isValid) {
            this.loadingVisible = true;
            if (this.formParam.noEndDate === false && !!this.formParam.endDate) {
                const beginDate = moment(this.formParam.effectiveDate);
                const endDate = moment(this.formParam.endDate);
                this.formParam.expireAfterDays = endDate.diff(beginDate, 'days');
            } else {
                this.formParam.expireAfterDays = null;
            }

            if (this.isOwnerContext) {
                const payload: AddEditOwnerAlertParameterDto = plainToClass(
                    AddEditOwnerAlertParameterDto,
                    JSON.parse(JSON.stringify(this.formParam))
                );
                if (!!this.formParam.alertID) {
                    this.alertsService.updateOwnerAlert(payload).subscribe(
                        (result) => {
                            this.loadingVisible = false;
                            this.displayResultInfo(result, 'updated');
                        },
                        (error) => {
                            this.loadingVisible = false;
                            this.errorService.displayError(error);
                        }
                    );
                } else {
                    this.alertsService.saveOwnerAlert(payload).subscribe(
                        (result) => {
                            this.loadingVisible = false;
                            this.displayResultInfo(result, 'added');
                        },
                        (error) => {
                            this.loadingVisible = false;
                            this.errorService.displayError(error);
                        }
                    );
                }
            } else {
                const payload: AddEditCommunityAlertParameterDto = plainToClass(
                    AddEditCommunityAlertParameterDto,
                    JSON.parse(JSON.stringify(this.formParam))
                );
                if (!!this.formParam.alertID) {
                    this.alertsService.updateCommunityAlert(payload).subscribe(
                        (result) => {
                            this.loadingVisible = false;
                            this.displayResultInfo(result, 'updated');
                        },
                        (error) => {
                            this.loadingVisible = false;
                            this.errorService.displayError(error);
                        }
                    );
                } else {
                    this.alertsService.saveCommunityAlert(payload).subscribe(
                        (result) => {
                            this.loadingVisible = false;
                            this.displayResultInfo(result, 'added');
                        },
                        (error) => {
                            this.loadingVisible = false;
                            this.errorService.displayError(error);
                        }
                    );
                }
            }
        }
    }

    onCancelClick(): void {
        this.formParam = null;
        this.alertForm.instance.resetValues();
        this.visible = false;
    }

    onEndDateFlagChange(): void {
        if (!!this.formParam && this.formParam.noEndDate) {
            this.formParam.endDate = null;
        }
    }

    private displayResultInfo(result: StatusMessageResultDto, status: string): void {
        if (!result.isSuccessMessage) {
            this.errorService.displayMessage(result.message, 'error');
        } else {
            notify(`Successfully ${status} the alert`, 'success', MessageInterval.successMsgInterval);
            this.onSuccess.emit(true);
            this.onCancelClick();
        }
    }

    private updateScreenSize(): void {
        this.isSmallScreen = this.screenService.isSmallScreen();
        if (this.isSmallScreen) {
            this.popupWidth = '95%';
            this.popupHeight = '90%';
        } else {
            this.popupWidth = '35%';
            this.popupHeight = '55%';
        }
    }
}
